import {Injectable, Injector} from '@angular/core';
import {Actions, Effect, ofType} from '@ngrx/effects';
import {Action, Store} from '@ngrx/store';
import {Observable} from 'rxjs';
import {catchError, map, switchMap, take, tap} from 'rxjs/operators';
import * as fetchBpmnDeploymentActions from '../actions/curation-info-for-pathways.actions';
import {
    UpdateBpmnNodeSelectionDepdendenciesCurationData,
    UpdateKnowledgeBase
} from '@ispm-client/modules/pathways/store';
import * as _ from 'lodash';
import {PathwaysService} from '../../services/pathways.service';
import {of} from 'rxjs/observable/of';
import {
    CurationInfoForPathwaysActionTypes,
    FetchClinicalTrialsAction,
    GetCurationItemById,
    HandlePathwaysDataFetchedByKeysAction,
    UpdateBpmnNodesCurationData,
    UpdateClinicalTrialsAction,
    UpdateCurationItemIdAction
} from '../actions';
import {PatientDataState} from '@ispm-client/modules/patient';
import {from} from 'rxjs';
import {UpdatePatientDataInStoreAction} from '@ispm-client/modules/process-executor/store';
import {PathwaysExecutionData} from '@ispm-client/modules/pathways';
import {PathwaysConfigurationService} from '../../services';
import {SetErrorAction} from '@ispm/shared/store/actions/error.actions';
import {
    failedToFetchClinicalTrialsMessage,
    failedToFetchCurationData,
    failedToReadCurationItemMessage
} from '../../models/constants';
import {forkJoin} from 'rxjs/observable/forkJoin';
import {LogService} from "@ispm/core/services/log.service";

@Injectable()
export class CurationInfoForPathwaysEffects {

  constructor(private actions$: Actions,
              private patientDataStateStore$: Store<PatientDataState>,
              private injector: Injector,
              private logService : LogService) {
  }

  @Effect()
  fetchCurationItemByKeysEffect$: Observable<Action> = this.actions$.pipe(
    ofType<fetchBpmnDeploymentActions.SearchCurationItemByKeysAction>(
      CurationInfoForPathwaysActionTypes.SEARCH_CURATION_ITEM_BY_KEYS
    ),
    tap((action) => this.sendLog('fetchCurationItemByKeysEffect$')),
    switchMap<any, any>((action) => {
      return forkJoin(
        Observable.of(action),
        this.getPathwaysConfigurationService().getCurationClinicalKeys().pipe(take(1)),
        this.getPathwaysConfigurationService().getKnowledgeBase().pipe(take(1)),
        this.getPathwaysConfigurationService().getBpmnType().pipe(take(1)),
        this.getPathwaysConfigurationService().getCurationItemApprovalStatuses().pipe(take(1))
      );
    }),
    switchMap(([action, clinicalKeys, knowledgeBase, bpmnType, approvalStatuses]) =>
      this.getPathwaysService().searchPathwaysExecutionData(knowledgeBase, bpmnType, clinicalKeys, approvalStatuses, this.getPathwaysConfigurationService().getCurrentUserOrg())
      .pipe(
        switchMap(
          (data) => [
            new HandlePathwaysDataFetchedByKeysAction({data: data}),
            ...this.updatePathwaysCurationInfo(data,knowledgeBase)
          ]),
        catchError(error => of(new SetErrorAction({
          error: error.message,
          devMessage: 'fetchCurationItemByKeysEffect$: Failed to search pathway execution data',
          userMessage: failedToFetchCurationData
        })))
      )
    ));

  @Effect()
  getCurationItemByIdEffect$: Observable<Action> = this.actions$.pipe(
    ofType<GetCurationItemById>(
      CurationInfoForPathwaysActionTypes.GET_CURATION_ITEM_BY_ID
    ),
    switchMap<any, any>((action) => {
      return forkJoin(
        Observable.of(action),
        this.getPathwaysConfigurationService().getKnowledgeBase().pipe(take(1)),
        this.getPathwaysConfigurationService().getBpmnType().pipe(take(1))
      );
    }),
    switchMap(([action, knowledgeBase, bpmnType]) =>
      this.getPathwaysService().getPathwaysExecutionData(
        action.payload.curationItemId, knowledgeBase, bpmnType)
        .pipe(
          switchMap(
            (data) => [
              ...this.updatePathwaysCurationInfo(data,knowledgeBase)
            ]),
          catchError(error => of(new SetErrorAction({
            error: error.message,
            devMessage: 'getCurationItemByIdEffect$: Failed to get pathway execution data by curation item ID',
            userMessage: failedToReadCurationItemMessage
          })))
        )
    )
  );

  @Effect()
  fetchClinicalTrialsEffect$: Observable<Action> = this.actions$.pipe(
    ofType<FetchClinicalTrialsAction>(
      CurationInfoForPathwaysActionTypes.FETCH_CLINICAL_TRIALS
    ),
    switchMap<any, any>((action) => {
        return forkJoin(
          Observable.of(action),
          this.getPathwaysConfigurationService().getBpmnLinkedResourcesApprovalStatuses().pipe(take(1)),
          this.getPathwaysConfigurationService().getBpmnLinkedResourcesType().pipe(take(1)),
          this.getPathwaysConfigurationService().getBpmnConnectingResourcesType().pipe(take(1)),
          this.getPathwaysConfigurationService().getKnowledgeBase().pipe(take(1)),
          this.getPathwaysConfigurationService().getBpmnConnectingResourcesApprovalStatuses().pipe(take(1)),
          this.getPathwaysConfigurationService().getClinicalTrialsSources().pipe(take(1)),
          this.getPathwaysConfigurationService().getClinicalTrialStatusSearchParams().pipe(take(1))
        );
      }
    ),
    switchMap(([
                 action,
                 bpmnLinkedResourcesApprovalStatuses,
                 bpmnLinkedResourcesType,
                 bpmnConnectingResourcesType,
                 knowledgeBase,
                 bpmnConnectingResourcesApprovalStatuses,
                 clinicalTrialsSource,
                 clinicalTrialStatusSearchParams
               ]) =>
      this.getPathwaysService().getClinicalTrials(
        action.payload.bpmnDeploymentIdentifier.deploymentId,
        bpmnLinkedResourcesApprovalStatuses,
        bpmnConnectingResourcesApprovalStatuses,
        bpmnLinkedResourcesType,
        bpmnConnectingResourcesType,
        knowledgeBase,
        clinicalTrialsSource,
        clinicalTrialStatusSearchParams
      )
        .pipe(
          switchMap((clinicalTrialsResults) =>
            [new UpdateClinicalTrialsAction({ nodeIdToClinicalTrials: clinicalTrialsResults.body})]
          ),
          catchError(error =>
            from([
              new UpdateClinicalTrialsAction({ nodeIdToClinicalTrials: { }}),
              new SetErrorAction({
                error,
                devMessage: 'fetchClinicalTrialsEffect$: Failed to get clinical trials for pathway',
                userMessage: failedToFetchClinicalTrialsMessage
              })
            ])
          )
        )
    )
  );

  private getPathwaysConfigurationService(): PathwaysConfigurationService {
    return this.injector.get(PathwaysConfigurationService);
  }

  private getPathwaysService(): PathwaysService {
    return this.injector.get(PathwaysService);
  }

  private updatePathwaysCurationInfo(pathwaysExecutionData: PathwaysExecutionData, knowledgeBase: Array<string>): Action[] {
    if (!_.isEmpty(pathwaysExecutionData)) {
      return [
        new UpdateCurationItemIdAction({curationItemId: pathwaysExecutionData.curationItemId}),
        new UpdateBpmnNodesCurationData({nodes: pathwaysExecutionData.nodes}),
        new UpdateBpmnNodeSelectionDepdendenciesCurationData({nodeSelectionDependencies: pathwaysExecutionData.nodeSelectionDependencies}),
        new UpdatePatientDataInStoreAction({patientData: pathwaysExecutionData.patientData}),
        new UpdateKnowledgeBase({knowledgeBase: knowledgeBase.join(',')})
      ];
    }
    else {
      return [];
    }
  }

  private sendLog(effect: string, paramsObj?: any) {
    let logMsg: string = '[CurationInfoForPathwaysEffects] ' + effect;
    Object.keys(paramsObj || {}).forEach((key) => logMsg += ' ' + key + '=' + paramsObj[key]);

    console.log(logMsg);
    this.logService.info(logMsg, '');
  }
}
